import { Box, ButtonBase } from '@mui/material';
import React from 'react';
import ChatImageWithFallback from 'ui-component/display/ChatImageWithFallback';
import ChatPdfDocumentTile from 'ui-component/display/ChatPdfDocumentTile';
import { CHAT_FILE_TYPE_ICONS, ChatDocumentKind } from 'ui-component/display/chatFileTypeAssets';
import { MessageMediaType, MessageStatus } from 'types/chat/messages/common';
import MediaPlaceholderWrapper from 'ui-component/display/MediaPlaceholderWrapper';
import LoupeOutlinedIcon from '@mui/icons-material/LoupeOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { isValidUrl } from 'utils/helpers';
import { resolveChatDocumentKind } from 'utils/chat-document-kind';
import FileUploadProgress from './FileUploadProgress';
import { MediaPreviewType } from 'types/chat/props';

interface LinkComponentProps {
  docKind: ChatDocumentKind;
  status: MessageStatus | null | undefined;
  handleMediaPreview: (params: Partial<MediaPreviewType>) => void;
  file: string;
  children?: React.ReactNode;
}

const PreviewComponent = ({ docKind, status, handleMediaPreview, file, children }: LinkComponentProps) =>
  docKind === 'pdf' ? (
    <ButtonBase
      sx={{
        maxWidth: '100%',
        maxHeight: '100%',
        display: 'block',
        borderRadius: 1,
        overflow: 'hidden',
        position: 'relative',
        cursor: 'zoom-in',
      }}
      onClick={() =>
        status !== 'uploading' &&
        isValidUrl(file) &&
        handleMediaPreview({
          visible: true,
          file: file,
          type: 'pdf',
        })
      }
    >
      {children}
    </ButtonBase>
  ) : (
    <Box
      component={'a'}
      target="_blank"
      rel="noreferrer"
      href={file}
      sx={{
        width: '100%',
        height: '100%',
        display: 'block',
        borderRadius: 1,
        overflow: 'hidden',
        position: 'relative',
        cursor: 'zoom-in',
      }}
    >
      {children}
    </Box>
  );

interface DocMessageProps {
  file: string;
  fileType: MessageMediaType;
  mediaNoteFormat: string | null;
  object: string | null | undefined;
  status: MessageStatus | null | undefined;
  handleMediaPreview: (params: Partial<MediaPreviewType>) => void;
}

const DOC_ICON_SRC: Partial<Record<ChatDocumentKind, string>> = {
  docx: CHAT_FILE_TYPE_ICONS.docx,
  xlsx: CHAT_FILE_TYPE_ICONS.xlsx,
  zip: CHAT_FILE_TYPE_ICONS.zip,
};

const DocMessage: React.FC<DocMessageProps> = ({
  file,
  fileType,
  mediaNoteFormat,
  object,
  status,
  handleMediaPreview,
}) => {
  const docKind = resolveChatDocumentKind(file, fileType, mediaNoteFormat, object);
  const otherDocIconSrc = docKind !== 'pdf' && docKind !== 'unknown' ? DOC_ICON_SRC[docKind] : undefined;

  return (
    <Box
      sx={{
        width: 110,
        height: 110,
        margin: '0 auto !important',
      }}
    >
      <PreviewComponent
        file={file}
        docKind={docKind}
        status={status}
        handleMediaPreview={handleMediaPreview}
      >
        {status === 'uploading' && <FileUploadProgress interval={300} />}
        {status !== 'uploading' && isValidUrl(file) && (
          <MediaPlaceholderWrapper isWaitUpload={false}>
            <LoupeOutlinedIcon
              sx={{
                fontSize: 40,
              }}
            />
          </MediaPlaceholderWrapper>
        )}
        {docKind === 'pdf' ? (
          <ChatPdfDocumentTile size={110} />
        ) : otherDocIconSrc ? (
          <ChatImageWithFallback
            src={otherDocIconSrc}
            alt=""
            sx={{
              width: '100%',
              height: '100%',
              borderRadius: 1,
              objectFit: 'contain',
              bgcolor: 'common.white',
            }}
          />
        ) : (
          <Box
            sx={{
              width: '100%',
              height: '100%',
              bgcolor: 'common.white',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <InsertDriveFileOutlinedIcon sx={{ fontSize: 44, color: 'text.secondary' }} aria-hidden />
          </Box>
        )}
      </PreviewComponent>
    </Box>
  );
};

export default DocMessage;
